import {
	Typography,
	Button,
	CircularProgress,
	Box,
	IconButton,
	Tooltip,
	Stack,
	Paper
} from '@mui/material';
import { useState, useMemo, useRef } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import DataTable from '@/components/data-table/DataTable';
import { type MRT_ColumnDef } from 'material-react-table';
import { useGetCardsListQuery, useDeleteCardMutation, useSetDefaultCardMutation } from '@/store/services/paymentMethod';
import { Card } from '../utilities/constants/constants';
import { enqueueSnackbar } from 'notistack';

function PaymentMethodPage() {
	const [pageCursors, setPageCursors] = useState<(string | null)[]>([null]);
	const [currentPageIndex, setCurrentPageIndex] = useState(0);
	const currentStartingAfter = pageCursors[currentPageIndex] ?? null;
	const { data: response, isLoading, isFetching } = useGetCardsListQuery(
		{
			starting_after: currentStartingAfter
		},
		{
			refetchOnMountOrArgChange: true
		}
	);
	const showTableLoading = isLoading || isFetching;
	const [deleteCard] = useDeleteCardMutation();
	const [defaultCard] = useSetDefaultCardMutation();
	const lastCardsRef = useRef<Card[]>([]);
	const [search, setSearch] = useState('');
	const [deletingCardId, setDeletingCardId] = useState<string | null>(null);
	const [settingDefaultCardId, setSettingDefaultCardId] = useState<string | null>(null);

	const cards = useMemo(() => {
		const paymentMethods = response?.data?.payment_methods;
		let next: Card[] = [];

		if (Array.isArray(paymentMethods) && paymentMethods.length > 0) {
			next = paymentMethods;
		} else if (Array.isArray(response?.data) && response.data.length > 0) {
			next = response.data;
		}

		if (next.length > 0) {
			lastCardsRef.current = next;
			return next;
		}

		if (isFetching && lastCardsRef.current.length > 0) {
			return lastCardsRef.current;
		}

		lastCardsRef.current = [];
		return [];
	}, [response, isFetching]);

	const filteredCards = useMemo(() => {
		const digits = search.replace(/\D/g, '');
		if (!digits) {
			return cards;
		}

		return cards.filter((card) => card.last4?.includes(digits));
	}, [cards, search]);

	const paginationData = response?.data?.pagination;
	const canGoNext = Boolean(paginationData?.has_more && paginationData?.next_cursor);
	const canGoPrevious = currentPageIndex > 0;
	const currentPage = currentPageIndex + 1;
	const hasKnownNextPage = currentPageIndex + 1 < pageCursors.length;
	const shouldShowNextPagePreview = canGoNext && !hasKnownNextPage;

	const handleNextPage = () => {
		if (!canGoNext || isFetching) return;
		setPageCursors((prev) => {
			if (prev[currentPageIndex + 1] === paginationData.next_cursor) {
				return prev;
			}
			const next = prev.slice(0, currentPageIndex + 1);
			next.push(paginationData.next_cursor);
			return next;
		});
		setCurrentPageIndex((prev) => prev + 1);
	};

	const handlePreviousPage = () => {
		if (!canGoPrevious || isFetching) return;
		setCurrentPageIndex((prev) => Math.max(prev - 1, 0));
	};

	const handleFirstPage = () => {
		if (isFetching || currentPageIndex === 0) return;
		setCurrentPageIndex(0);
	};

	const handleLastKnownPage = () => {
		if (isFetching || !canGoNext) return;
		handleNextPage();
	};

	const handleGoToPage = (targetPage: number) => {
		const targetIndex = targetPage - 1;
		if (isFetching || targetIndex < 0 || targetIndex >= pageCursors.length) return;
		setCurrentPageIndex(targetIndex);
	};

	const handleDeleteCard = async (paymentMethodId: string) => {
		setDeletingCardId(paymentMethodId);
		try {
			await deleteCard({ paymentMethodId }).unwrap();
			enqueueSnackbar('Card deleted successfully.', { variant: 'success' });
		} catch (error) {
			const typedError = error as any;
			enqueueSnackbar(typedError?.data?.errors?.[0]?.detail || 'An error occurred', {
				variant: 'error'
			});
		} finally {
			setDeletingCardId(null);
		}
	};

	const handleDefaultCard = async (paymentMethodId: string) => {
		setSettingDefaultCardId(paymentMethodId);
		try {
			await defaultCard({ paymentMethodId }).unwrap();
			enqueueSnackbar('Card set as default successfully.', { variant: 'success' });
		} catch (error) {
			const typedError = error as any;
			enqueueSnackbar(typedError?.data?.errors?.[0]?.detail || 'An error occurred', {
				variant: 'error'
			});
		} finally {
			setSettingDefaultCardId(null);
		}
	};

	const columns = useMemo<MRT_ColumnDef<Card>[]>(
		() => [
			{
				accessorKey: 'display_brand',
				header: 'Card',
				enableColumnDragging: false,
				enableColumnActions: false,
				Cell: ({ row }) => {
					const card = row.original;
					return (
						<div className="flex items-center space-x-4">
							{card.image ? (
								<img
									src={card.image}
									alt={card.display_brand}
									className="h-9 w-9 object-contain"
								/>
							) : (
								<Box className="h-9 w-9 bg-black p-1 object-contain flex items-center justify-center">
									<img
										src={
											'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAACgUlEQVR4nO3Ya4tNURjA8SleeCO+ge/Aa/kIYhAzjFvG/TaDGcb9fr9fIuUF80IpmXHGYMgtYdwSibyWiK/w065n6iQnS41mlf3U0+6cs/dZ//+znrXXPqeurowyyiijjDJ+ExiBFlxHP17G8Ske4yHu4S5uoxcVdOEqruAyOnERF3AeZ3Eax3EYB7AXu7AVG7Eeq9GIsRhW9zeBMQF1H5dwBidxAsdwFEcC4FBA7Me+gNmD3diJHdiObQG4BZsCtAMbArgN67AWa9AaWRSxCSP/pvIFfHdU6tQQwbdgFVbGbMxOmom48H5G8CuwPN4blyJwPdomJ/hlcZyVIvBsCHu+pQb8UiwpPksReJkh/GIsKs5PEejPFH5hcV2KwA98wkd8wHu8w1u8wWu8wouqfeFJ7A2P8KBqf+jDLdxET6yv6qxE/vq6Vt5NEfieKXwlVeBbpvCV4vtTBL5mCt+TKvAlU/ieYoNNEficKXxPMV6KQO8Q3yqbsQDzMQ9zMSeehdanCFQyhW9KFejKFH5mMU6KwNVM4RtTBa7UgD+H57Gw+2Mh34vfDgML7gauRQEGG76hKFiKwOUale9PgO+NO1H3P4CfUcx6ikBnjbZ5nAjfF7fJwYafnipwsUbPF0+odxLhd/8D+GmpAhcyWbANv8BPxeYUgfOZwk9JFTibKXx9wZUicDpT+MlFZ6QIHAyB3OCnpG5krdFCOcFPivMWpQhMiH0gJ/iJMe74FIHhAdWREXxzjDf8jwIhMSouag+BoYCvj/NaYrzRSfBVEsPir+3GgG6NmRjItb/JdVVt1xbZHu04kBsiixneWJWbI7fEPtQRRRmfXPkyyiijjDL+u/gJNGBbn5+LIh4AAAAASUVORK5CYII='
										}
										alt={card.display_brand}
										className="h-10 w-10 object-contain"
									/>
								</Box>
							)}
							<div>
								<Typography
									variant="subtitle2"
									className="font-semibold text-gray-900 capitalize"
								>
									{card.display_brand}
								</Typography>
								<Typography
									variant="body2"
									className="text-gray-500"
								>
									**** {card.last4}
								</Typography>
							</div>
						</div>
					);
				}
			},
			{
				accessorKey: 'exp_year',
				header: 'Details',
				enableColumnDragging: false,
				enableColumnActions: false,
				Cell: ({ row }) => (
					<>
						<Typography
							variant="body2"
							className="text-gray-700"
						>
							Expires {row.original.exp_month}/{row.original.exp_year}
						</Typography>
						<Typography
							variant="body2"
							className="text-gray-500 text-sm"
						>
							{row.original.country}
						</Typography>
					</>
				)
			},
			{
				accessorKey: 'default',
				header: 'Status',
				enableColumnDragging: false,
				enableColumnActions: false,
				Cell: ({ row }) =>
					row.original.default ? (
						<span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
							Default
						</span>
					) : (
						<span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
							Secondary
						</span>
					)
			},
			{
				id: 'actions',
				header: 'Actions',
				enableColumnFilter: false,
				enableColumnDragging: false,
				enableSorting: false,
				enableColumnActions: false,
				muiTableHeadCellProps: { align: 'right' },
				size: 220,
				Cell: ({ row }) => {
					const card = row.original;
					return (
						<div className="flex items-center justify-end space-x-2">
							{!card.default && (
								<Button
									variant="contained"
									size="small"
									color="secondary"
									onClick={() => handleDefaultCard(card.payment_method_id)}
									disabled={settingDefaultCardId !== null}
									sx={{
										minHeight: 'unset',
										height: '26px',
										py: 0.25,
										px: 1,
										fontSize: '10px',
										lineHeight: 1.2,
										borderRadius: '6px',
										'& .MuiButton-startIcon': { mr: 0.375, ml: -0.25 }
									}}
									startIcon={
										settingDefaultCardId === card.payment_method_id && (
											<CircularProgress
												size={12}
												color="inherit"
											/>
										)
									}
								>
									Set as Default
								</Button>
							)}
							<Tooltip title={!card.default ? 'Delete Card' : 'Default card cannot be deleted'}>
								<span>
									<IconButton
										size="small"
										color="error"
										aria-label="Delete card"
										onClick={() => handleDeleteCard(card.payment_method_id)}
										disabled={deletingCardId !== null || card.default}
									>
										{deletingCardId === card.payment_method_id ? (
											<CircularProgress
												size={20}
												color="inherit"
											/>
										) : (
											<DeleteIcon fontSize="small" />
										)}
									</IconButton>
								</span>
							</Tooltip>
						</div>
					);
				}
			}
		],
		[deletingCardId, settingDefaultCardId]
	);

	const showEmptyState = !isLoading && !isFetching && cards.length === 0;

	const paginationFooter = cards.length > 0 && (
		<Stack
			direction="row"
			alignItems="center"
			justifyContent="end"
			className="shrink-0 sticky bottom-0 z-10 bg-white border-t border-gray-200 px-6 py-4"
		>
			<div className="flex items-center gap-1">
				<IconButton
					size="small"
					onClick={handleFirstPage}
					disabled={!canGoPrevious || isFetching}
					sx={{ border: '1px solid #d1d5db', borderRadius: '4px' }}
				>
					<FuseSvgIcon size={14}>heroicons-outline:chevron-double-left</FuseSvgIcon>
				</IconButton>
				<IconButton
					size="small"
					onClick={handlePreviousPage}
					disabled={!canGoPrevious || isFetching}
					sx={{ border: '1px solid #d1d5db', borderRadius: '4px' }}
				>
					<FuseSvgIcon size={14}>heroicons-outline:chevron-left</FuseSvgIcon>
				</IconButton>

				{pageCursors.map((_, index) => {
					const pageNumber = index + 1;
					const isActive = pageNumber === currentPage;
					return (
						<Button
							key={`page-${pageNumber}`}
							size="small"
							onClick={() => handleGoToPage(pageNumber)}
							disabled={isFetching}
							variant={isActive ? 'contained' : 'outlined'}
							color={isActive ? 'secondary' : 'inherit'}
							sx={{
								minWidth: 34,
								height: 32,
								borderRadius: '4px',
								borderColor: '#d1d5db',
								...(isActive
									? { color: '#111827', backgroundColor: '#fef3c7' }
									: { color: '#111827' })
							}}
						>
							{pageNumber}
						</Button>
					);
				})}

				{shouldShowNextPagePreview && (
					<>
						<Typography
							variant="body2"
							sx={{ px: 0.5, color: 'text.secondary' }}
						>
							...
						</Typography>
						<Button
							size="small"
							onClick={handleNextPage}
							disabled={isFetching}
							variant="outlined"
							sx={{
								minWidth: 34,
								height: 32,
								borderRadius: '4px',
								borderColor: '#d1d5db',
								color: '#111827'
							}}
						>
							{currentPage + 1}
						</Button>
					</>
				)}

				<IconButton
					size="small"
					onClick={handleNextPage}
					disabled={!canGoNext || isFetching}
					sx={{ border: '1px solid #d1d5db', borderRadius: '4px' }}
				>
					<FuseSvgIcon size={14}>heroicons-outline:chevron-right</FuseSvgIcon>
				</IconButton>
				<IconButton
					size="small"
					onClick={handleLastKnownPage}
					disabled={!canGoNext || isFetching}
					sx={{ border: '1px solid #d1d5db', borderRadius: '4px' }}
				>
					<FuseSvgIcon size={14}>heroicons-outline:chevron-double-right</FuseSvgIcon>
				</IconButton>
			</div>
		</Stack>
	);

	return (
		<div className="relative max-w-6xl mx-auto w-full">
			<Paper
				className="flex flex-col flex-auto shadow-1 rounded-lg overflow-hidden w-full h-[calc(100vh-146px)]"
				elevation={0}
			>
				{showEmptyState ? (
					<div className="flex flex-1 items-center justify-center p-12 text-center">
						<Typography
							variant="body2"
							className="text-gray-500 py-8"
						>
							No payment methods found. Add your first payment method using the button above.
						</Typography>
					</div>
				) : (
					<div className="flex flex-col flex-1 min-h-0">
						<div className="flex flex-col flex-1 min-h-0 overflow-hidden">
							<DataTable
								data={filteredCards}
								columns={columns}
								emptyMessage="No payment methods found. Add your first payment method using the button above."
								query={search}
								setQuery={(value) =>
									setSearch(value.replace(/\D/g, '').slice(0, 4))
								}
								searchPlaceholder="Search by last 4 digits"
								enableGlobalFilter
								enableTopToolbar
								enableBottomToolbar={false}
								enablePagination={false}
								enableRowSelection={false}
								enableRowActions={false}
								enableColumnFilters={false}
								enableSorting={false}
								enableDensityToggle={false}
								enableHiding={false}
								enableToolbarInternalActions={false}
								enableStickyHeader
								muiTablePaperProps={{
									elevation: 0,
									square: true,
									sx: {
										display: 'flex',
										flexDirection: 'column',
										flex: 1,
										minHeight: 0,
										height: '100%',
										overflow: 'hidden',
										boxShadow: 'none'
									}
								}}
								muiTableContainerProps={{
									sx: {
										flex: 1,
										minHeight: 0,
										maxHeight: '100%',
										overflow: 'auto'
									}
								}}
								muiTableHeadRowProps={{ sx: { backgroundColor: '#f9fafb' } }}
								muiTableHeadCellProps={{ sx: { backgroundColor: '#f9fafb' } }}
								muiTableBodyProps={{ sx: { backgroundColor: '#ffffff' } }}
								muiTableBodyCellProps={{ sx: { py: showTableLoading ? 2.5 : 1.4 } }}
								state={{
									isLoading: showTableLoading,
									showLoadingOverlay: showTableLoading,
									showSkeletons: showTableLoading
								}}
							/>
						</div>
						{paginationFooter}
					</div>
				)}
			</Paper>
		</div>
	);
}

export default PaymentMethodPage;